import React, { useState, useEffect } from "react";
import {
  Card,
  Input,
  Button,
  Space,
  Typography,
  message,
  Modal,
  Tag,
  Divider,
  Switch,
  Form,
  Tooltip,
} from "antd";
import {
  SettingOutlined,
  CopyOutlined,
  EditOutlined,
  KeyOutlined,
  LogoutOutlined,
  MailOutlined,
  InfoCircleOutlined,
  WalletOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import { apiClient, API_ENDPOINTS } from "../../../utils/api";
import styled from "styled-components";
import BirthdayEmailConfigModal from "./BirthdayEmailConfigModal";

const COLORS = {
  bgPrimary: "#0D1117",
  bgSecondary: "#161B22",
  border: "#1f2a37",
  textPrimary: "#E2E8F0",
  textSecondary: "#94A3B8",
  accent: "#22D3EE",
  accentDim: "rgba(34, 211, 238, 0.1)",
  hover: "#1f2a37",
};

const { Title, Text } = Typography;
const { TextArea } = Input;

const PageContainer = styled.div`
  padding: 0;
  color: ${COLORS.textPrimary};
`;

const HeaderContainer = styled.div`
  margin-bottom: 24px;

  h2 {
    margin-bottom: 8px !important;
    font-size: 24px !important;
    font-weight: 600 !important;
    color: ${COLORS.textPrimary} !important;
  }

  p {
    color: ${COLORS.textSecondary};
    margin: 0;
  }
`;

const StyledCard = styled(Card)`
  background: ${COLORS.bgSecondary} !important;
  border: 1px solid ${COLORS.border} !important;
  margin-bottom: 24px;

  .ant-card-head {
    border-bottom: 1px solid ${COLORS.border} !important;

    .ant-card-head-title {
      color: ${COLORS.textPrimary} !important;
    }
  }

  .ant-card-body {
    color: ${COLORS.textPrimary} !important;
  }
`;

const SettingItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid ${COLORS.border};

  &:last-child {
    border-bottom: none;
  }

  .setting-info {
    flex: 1;

    .setting-label {
      font-weight: 600;
      color: ${COLORS.textPrimary};
      margin-bottom: 4px;
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .setting-description {
      color: ${COLORS.textSecondary};
      font-size: 12px;
      margin-bottom: 0;
    }

    .setting-value {
      color: ${COLORS.textPrimary};
      font-family: monospace;
      background: ${COLORS.bgPrimary};
      padding: 6px 10px;
      border-radius: 6px;
      border: 1px solid ${COLORS.border};
      word-break: break-all;
      font-size: 13px;
      margin-top: 8px;
    }
  }
`;

// Add CSS animation for spinner
const spinnerAnimation = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// Inject the animation
if (typeof document !== "undefined") {
  const style = document.createElement("style");
  style.textContent = spinnerAnimation;
  document.head.appendChild(style);
}

const Settings: React.FC = () => {
  const [settings, setSettings] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingSetting, setEditingSetting] = useState<any>(null);
  const [editValue, setEditValue] = useState("");
  const [rsaPublicKey, setRsaPublicKey] = useState("");
  const [isRsaPrivateKeySet, setIsRsaPrivateKeySet] = useState(false);
  const [webhookIPs, setWebhookIPs] = useState<string[]>([]);
  const [webhookIPsInput, setWebhookIPsInput] = useState("");
  const [discoveredWatcherIPs, setDiscoveredWatcherIPs] = useState<string[]>(
    [],
  );
  const [selectedDiscoveredIPs, setSelectedDiscoveredIPs] = useState<string[]>(
    [],
  );
  const [rsaConfirmModalVisible, setRsaConfirmModalVisible] = useState(false);
  const [syncingRsaKey, setSyncingRsaKey] = useState(false);
  const [webhookIPsModalVisible, setWebhookIPsModalVisible] = useState(false);
  const [logoutAllAdminsModalVisible, setLogoutAllAdminsModalVisible] =
    useState(false);
  const [logoutAllMerchantsModalVisible, setLogoutAllMerchantsModalVisible] =
    useState(false);
  const [birthdayEmailConfigVisible, setBirthdayEmailConfigVisible] =
    useState(false);
  const [defaultMerchant, setDefaultMerchant] = useState<any>(null);
  const [defaultMerchantLoading, setDefaultMerchantLoading] = useState(false);
  const [custodialWalletsForm] = Form.useForm();

  useEffect(() => {
    fetchSettings();
    fetchRSAPublicKey();
    fetchDefaultMerchant();
    fetchWebhookIPs();
    fetchDiscoveredWatcherIPs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchWebhookIPs = async () => {
    try {
      const response = await apiClient.get(API_ENDPOINTS.ADMIN_GET_WEBHOOK_IPS);
      if (response.data?.status === "success" && response.data?.data) {
        setWebhookIPs(response.data.data.webhookIPs || []);
      }
    } catch (error: any) {
      console.error("Error fetching webhook IPs:", error);
    }
  };

  const fetchDiscoveredWatcherIPs = async () => {
    try {
      const response = await apiClient.get(
        API_ENDPOINTS.ADMIN_GET_DISCOVERED_WATCHER_IPS,
      );
      if (response.data?.status === "success" && response.data?.data) {
        setDiscoveredWatcherIPs(response.data.data.discoveredIPs || []);
      }
    } catch (error: any) {
      console.error("Error fetching discovered watcher IPs:", error);
    }
  };

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(API_ENDPOINTS.ADMIN_GET_SETTINGS);
      if (response.data?.status === "success" && response.data?.data) {
        // Response.data is an array of settings
        const settingsArray = Array.isArray(response.data.data)
          ? response.data.data
          : response.data.data.settings || [];
        setSettings(settingsArray);
      }
    } catch (error: any) {
      message.error("Failed to fetch settings");
      console.error("Error fetching settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRSAPublicKey = async () => {
    try {
      const response = await apiClient.get(
        API_ENDPOINTS.ADMIN_GET_RSA_PUBLIC_KEY,
      );
      if (response.data?.status === "success" && response.data?.data) {
        setRsaPublicKey(response.data.data.publicKey || "");
        setIsRsaPrivateKeySet(response.data.data.isRsaPrivateKeySet || false);
      }
    } catch (error: any) {
      console.error("Error fetching RSA public key:", error);
    }
  };

  const handleEdit = (setting: any) => {
    setEditingSetting(setting);
    setEditValue(setting.value || "");
    setEditModalVisible(true);
  };

  const handleSave = async () => {
    if (!editingSetting) return;

    try {
      await apiClient.put(
        (API_ENDPOINTS.ADMIN_UPDATE_SETTING || '/api/Administrator/app-settings/{key}/update').replace('{key}', editingSetting.key),
        { value: editValue },
      );
      message.success("Setting updated successfully");
      setEditModalVisible(false);
      fetchSettings();
    } catch (error: any) {
      message.error("Failed to update setting");
      console.error("Error updating setting:", error);
    }
  };

  const handleToggleSetting = async (setting: any, newValue: boolean) => {
    try {
      await apiClient.put(
        (API_ENDPOINTS.ADMIN_UPDATE_SETTING || '/api/Administrator/app-settings/{key}/update').replace('{key}', setting.key),
        { value: String(newValue) },
      );
      message.success(`${setting.key} updated successfully`);
      fetchSettings();
    } catch (error: any) {
      message.error("Failed to update setting");
      console.error("Error updating setting:", error);
      // Revert the toggle on error
      fetchSettings();
    }
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    message.success("Copied to clipboard");
  };

  const handleGenerateRSAKeyPair = () => {
    setRsaConfirmModalVisible(true);
  };

  const confirmGenerateRSAKeyPair = async () => {
    try {
      await apiClient.post(API_ENDPOINTS.ADMIN_UPDATE_RSA_KEY_PAIR);
      message.success("RSA key pair generated successfully");
      setRsaConfirmModalVisible(false);
      fetchRSAPublicKey();
    } catch (error: any) {
      message.error("Failed to generate RSA key pair");
      console.error("Error generating RSA key pair:", error);
    }
  };

  const handleSyncRSAKeyToWatcher = async () => {
    try {
      setSyncingRsaKey(true);
      await apiClient.post(API_ENDPOINTS.ADMIN_SYNC_RSA_KEY_TO_WATCHER);
      message.success("RSA key synced to watcher successfully");
    } catch (error: any) {
      console.error("Error syncing RSA key to watcher:", error);
      message.error("Failed to sync RSA key to watcher");
    } finally {
      setSyncingRsaKey(false);
    }
  };

  const handleLogoutAllAdmins = async () => {
    try {
      const response = await apiClient.post(
        API_ENDPOINTS.ADMIN_LOGOUT_ALL_ADMINS,
      );
      if (response.data?.status === "success") {
        message.success(
          `Successfully logged out ${response.data.data?.loggedOutCount || 0} administrators`,
        );
        setLogoutAllAdminsModalVisible(false);
      }
    } catch (error: any) {
      message.error("Failed to logout all administrators");
      console.error("Error logging out all admins:", error);
    }
  };

  const handleLogoutAllMerchants = async () => {
    try {
      const response = await apiClient.post(
        API_ENDPOINTS.ADMIN_LOGOUT_ALL_MERCHANTS,
      );
      if (response.data?.status === "success") {
        message.success(
          `Successfully logged out ${response.data.data?.loggedOutCount || 0} merchants`,
        );
        setLogoutAllMerchantsModalVisible(false);
      }
    } catch (error: any) {
      message.error("Failed to logout all merchants");
      console.error("Error logging out all merchants:", error);
    }
  };

  const fetchDefaultMerchant = async () => {
    try {
      setDefaultMerchantLoading(true);
      const response = await apiClient.get(
        API_ENDPOINTS.ADMIN_GET_DEFAULT_MERCHANT,
      );
      if (response.data?.status === "success" && response.data?.data) {
        setDefaultMerchant(response.data.data);
        custodialWalletsForm.setFieldsValue({
          custodialEVMAddress: response.data.data.custodialEVMAddress || "",
          bitcoinCustodialWallet:
            response.data.data.bitcoinCustodialWallet || "",
          solanaCustodialWallet: response.data.data.solanaCustodialWallet || "",
          tronCustodialWallet: response.data.data.tronCustodialWallet || "",
        });
      }
    } catch (error: any) {
      console.error("Error fetching default merchant:", error);
      if (error.response?.status !== 404) {
        message.error("Failed to fetch default merchant");
      }
    } finally {
      setDefaultMerchantLoading(false);
    }
  };

  const handleUpdateCustodialWallets = async (values: any) => {
    try {
      setDefaultMerchantLoading(true);
      const response = await apiClient.post(
        API_ENDPOINTS.ADMIN_UPDATE_DEFAULT_MERCHANT_CUSTODIAL_WALLETS,
        {
          custodialEVMAddress: values.custodialEVMAddress || null,
          bitcoinCustodialWallet: values.bitcoinCustodialWallet || null,
          solanaCustodialWallet: values.solanaCustodialWallet || null,
          tronCustodialWallet: values.tronCustodialWallet || null,
        },
      );
      if (response.data?.status === "success") {
        message.success(
          "Default merchant custodial wallets updated successfully",
        );
        fetchDefaultMerchant();
      }
    } catch (error: any) {
      message.error(
        error.response?.data?.message || "Failed to update custodial wallets",
      );
      console.error("Error updating custodial wallets:", error);
    } finally {
      setDefaultMerchantLoading(false);
    }
  };

  const handleUpdateWebhookIPs = async () => {
    try {
      // Split by comma, newline, or space and filter out empty strings
      const ips = webhookIPsInput
        .split(/[\n,\s]+/)
        .map((ip) => ip.trim())
        .filter((ip) => ip.length > 0);

      const response = await apiClient.put(
        API_ENDPOINTS.ADMIN_UPDATE_WEBHOOK_IPS,
        {
          webhookIPs: ips,
        },
      );

      if (response.data?.status === "success") {
        message.success("Webhook IPs updated successfully");
        setWebhookIPsModalVisible(false);
        setWebhookIPsInput("");
        fetchWebhookIPs();
        fetchDiscoveredWatcherIPs(); // Refresh discovered IPs after update
      }
    } catch (error: any) {
      message.error(
        error.response?.data?.message || "Failed to update webhook IPs",
      );
      console.error("Error updating webhook IPs:", error);
    }
  };

  const handleApproveDiscoveredIPs = async () => {
    if (selectedDiscoveredIPs.length === 0) {
      message.warning("Please select IPs to approve");
      return;
    }

    try {
      const response = await apiClient.post(
        API_ENDPOINTS.ADMIN_APPROVE_DISCOVERED_IPS,
        {
          ipsToApprove: selectedDiscoveredIPs,
        },
      );
      if (response.data?.status === "success") {
        message.success(
          `${selectedDiscoveredIPs.length} IP(s) approved and added to webhook IPs`,
        );
        setSelectedDiscoveredIPs([]);
        // Refresh both lists
        fetchWebhookIPs();
        fetchDiscoveredWatcherIPs();
      } else {
        message.error(
          response.data?.message || "Failed to approve discovered IPs",
        );
      }
    } catch (error: any) {
      console.error("Error approving discovered IPs:", error);
      message.error(
        error.response?.data?.message || "Failed to approve discovered IPs",
      );
    }
  };

  const openWebhookIPsModal = () => {
    setWebhookIPsInput(webhookIPs.join("\n"));
    setWebhookIPsModalVisible(true);
  };

  const isBooleanSetting = (value: string | null): boolean => {
    if (!value) return false;
    const lowerValue = value.toLowerCase();
    return lowerValue === "true" || lowerValue === "false";
  };

  const getBooleanValue = (value: string | null): boolean => {
    if (!value) return false;
    return value.toLowerCase() === "true";
  };

  const renderSettingValue = (setting: any) => {
    if (isBooleanSetting(setting.value)) {
      return (
        <Switch
          checked={getBooleanValue(setting.value)}
          onChange={(checked) => handleToggleSetting(setting, checked)}
        />
      );
    }

    if (setting.value && setting.value.length > 100) {
      return (
        <div>
          <TextArea
            value={setting.value}
            readOnly
            rows={3}
            style={{
              background: COLORS.bgPrimary,
              color: COLORS.textPrimary,
              borderColor: COLORS.border,
              fontSize: "12px",
            }}
          />
          <Button
            icon={<CopyOutlined />}
            onClick={() => handleCopy(setting.value)}
            style={{ marginTop: 8 }}
            size="small"
          >
            Copy
          </Button>
        </div>
      );
    }

    return (
      <div className="setting-value">
        {setting.value || <Text type="secondary">Not set</Text>}
      </div>
    );
  };

  return (
    <PageContainer>
      <HeaderContainer>
        <Title level={2}>
          <SettingOutlined /> Settings
        </Title>
        <Text type="secondary">
          Manage application settings and API configurations
        </Text>
      </HeaderContainer>

      {/* RSA Key Section */}
      <StyledCard
        title={
          <span>
            <KeyOutlined /> RSA Key Pair
          </span>
        }
        loading={loading}
      >
        <SettingItem>
          <div className="setting-info">
            <div className="setting-label">Public Key</div>
            <div className="setting-description">
              Public key for merchants to verify webhook signatures
            </div>
            {rsaPublicKey ? (
              <div>
                <TextArea
                  value={rsaPublicKey}
                  readOnly
                  rows={6}
                  style={{
                    background: COLORS.bgPrimary,
                    color: COLORS.textPrimary,
                    borderColor: COLORS.border,
                    fontFamily: "monospace",
                    fontSize: "12px",
                  }}
                />
                <Button
                  icon={<CopyOutlined />}
                  onClick={() => handleCopy(rsaPublicKey)}
                  style={{ marginTop: 8 }}
                >
                  Copy Public Key
                </Button>
              </div>
            ) : (
              <Text type="secondary">No public key set</Text>
            )}
          </div>
        </SettingItem>
        <Divider />
        <Space>
          <Button
            type="primary"
            onClick={handleGenerateRSAKeyPair}
            icon={<KeyOutlined />}
          >
            Generate New RSA Key Pair
          </Button>
          <Button
            onClick={handleSyncRSAKeyToWatcher}
            loading={syncingRsaKey}
            disabled={!isRsaPrivateKeySet}
            icon={<LogoutOutlined />}
          >
            Sync Key to Watcher
          </Button>
          {isRsaPrivateKeySet && <Tag color="green">Private key is set</Tag>}
        </Space>
      </StyledCard>

      {/* Webhook IPs Section */}
      <StyledCard title={<span>🌐 Webhook IPs</span>}>
        <SettingItem>
          <div className="setting-info">
            <div className="setting-label">Allowed Webhook IPs</div>
            <div className="setting-description">
              IP addresses from which webhooks will be sent. Merchants can view
              these IPs to configure their firewalls.
            </div>
            <div className="setting-value">
              {webhookIPs.length > 0 ? (
                <div>
                  {webhookIPs.map((ip, index) => (
                    <Tag key={index} style={{ margin: "2px" }}>
                      {ip}
                    </Tag>
                  ))}
                </div>
              ) : (
                <Text type="secondary">No webhook IPs configured</Text>
              )}
            </div>
          </div>
          <Button
            type="primary"
            onClick={openWebhookIPsModal}
            icon={<EditOutlined />}
          >
            Configure IPs
          </Button>
        </SettingItem>
      </StyledCard>

      {/* Discovered Watcher IPs */}
      <StyledCard title={<span>🔍 Discovered OnchainWatcher IPs</span>}>
        <SettingItem>
          <div className="setting-info">
            <div className="setting-label">Pending IP Approvals</div>
            <div className="setting-description">
              IPs automatically discovered from OnchainWatcher sync requests.
              Approve them to add to the merchant-visible webhook IPs list.
            </div>
            <div className="setting-value">
              {discoveredWatcherIPs.length > 0 ? (
                <div style={{ marginTop: 12 }}>
                  <div style={{ marginBottom: 8 }}>
                    <Text strong>Select IPs to approve:</Text>
                  </div>
                  <Space direction="vertical" style={{ width: "100%" }}>
                    {discoveredWatcherIPs.map((ip, index) => (
                      <div
                        key={index}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={selectedDiscoveredIPs.includes(ip)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedDiscoveredIPs([
                                ...selectedDiscoveredIPs,
                                ip,
                              ]);
                            } else {
                              setSelectedDiscoveredIPs(
                                selectedDiscoveredIPs.filter(
                                  (selected) => selected !== ip,
                                ),
                              );
                            }
                          }}
                        />
                        <Tag color="orange">{ip}</Tag>
                        <Text type="secondary" style={{ fontSize: "12px" }}>
                          Discovered during sync
                        </Text>
                      </div>
                    ))}
                  </Space>
                </div>
              ) : (
                <Text type="secondary">No discovered IPs pending approval</Text>
              )}
            </div>
          </div>
          {discoveredWatcherIPs.length > 0 && (
            <Button
              type="primary"
              onClick={handleApproveDiscoveredIPs}
              disabled={selectedDiscoveredIPs.length === 0}
              icon={<SaveOutlined />}
            >
              Approve Selected ({selectedDiscoveredIPs.length})
            </Button>
          )}
        </SettingItem>
      </StyledCard>

      {/* Security Actions */}
      <StyledCard title="Security Actions">
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          <div>
            <div className="setting-label" style={{ marginBottom: 8 }}>
              <LogoutOutlined /> Logout All Administrators
            </div>
            <div className="setting-description" style={{ marginBottom: 12 }}>
              Force logout all active administrators except yourself. This will
              invalidate all their active sessions.
            </div>
            <Button
              danger
              icon={<LogoutOutlined />}
              onClick={() => setLogoutAllAdminsModalVisible(true)}
            >
              Logout All Admins
            </Button>
          </div>
          <Divider style={{ margin: "16px 0" }} />
          <div>
            <div className="setting-label" style={{ marginBottom: 8 }}>
              <LogoutOutlined /> Logout All Merchants
            </div>
            <div className="setting-description" style={{ marginBottom: 12 }}>
              Force logout all active merchants. This will invalidate all their
              active sessions.
            </div>
            <Button
              danger
              icon={<LogoutOutlined />}
              onClick={() => setLogoutAllMerchantsModalVisible(true)}
            >
              Logout All Merchants
            </Button>
          </div>
        </Space>
      </StyledCard>

      {/* Birthday Email Configuration */}
      <StyledCard title="Birthday Email Configuration">
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          <div>
            <div className="setting-label" style={{ marginBottom: 8 }}>
              <MailOutlined /> Birthday Messages
            </div>
            <div className="setting-description" style={{ marginBottom: 12 }}>
              Configure birthday email templates and settings. Manage email
              subjects, messages, and manually trigger birthday emails.
            </div>
            <Button
              type="primary"
              icon={<MailOutlined />}
              onClick={() => setBirthdayEmailConfigVisible(true)}
            >
              Configure Birthday Messages
            </Button>
          </div>
        </Space>
      </StyledCard>

      {/* Default Merchant Custodial Wallets */}
      <StyledCard
        title={
          <Space>
            <WalletOutlined /> Default Merchant Custodial Wallets
            <Tooltip
              title={
                <div style={{ maxWidth: 400 }}>
                  <div style={{ marginBottom: 8, fontWeight: "bold" }}>
                    How Custodial Wallets Work:
                  </div>
                  <div style={{ marginBottom: 4 }}>
                    • The custodial wallet receives all invoice payments from
                    merchants
                  </div>
                  <div style={{ marginBottom: 4 }}>
                    • Internal wallet transactions are automatically swept to
                    this wallet
                  </div>
                  <div style={{ marginBottom: 4 }}>
                    • Admin must provide gas to sweep funds from internal
                    wallets
                  </div>
                  <div style={{ marginBottom: 4 }}>
                    • Gas and Internal wallets are owned by the Default Merchant
                    (system-owned)
                  </div>
                  <div>• Only Super Admins can configure these wallets</div>
                </div>
              }
              placement="right"
            >
              <InfoCircleOutlined
                style={{ color: COLORS.accent, cursor: "help" }}
              />
            </Tooltip>
          </Space>
        }
        loading={defaultMerchantLoading}
      >
        {defaultMerchant ? (
          <Form
            form={custodialWalletsForm}
            onFinish={handleUpdateCustodialWallets}
            layout="vertical"
          >
            <Form.Item
              label="EVM Custodial Wallet Address"
              name="custodialEVMAddress"
              tooltip="Ethereum-compatible blockchain addresses (EVM chains like Ethereum, BSC, Polygon, etc.)"
            >
              <Input
                placeholder="0x..."
                style={{
                  background: COLORS.bgPrimary,
                  color: COLORS.textPrimary,
                  borderColor: COLORS.border,
                }}
              />
            </Form.Item>

            <Form.Item
              label="Bitcoin Custodial Wallet Address"
              name="bitcoinCustodialWallet"
              tooltip="Bitcoin blockchain address"
            >
              <Input
                placeholder="bc1..."
                style={{
                  background: COLORS.bgPrimary,
                  color: COLORS.textPrimary,
                  borderColor: COLORS.border,
                }}
              />
            </Form.Item>

            <Form.Item
              label="Solana Custodial Wallet Address"
              name="solanaCustodialWallet"
              tooltip="Solana blockchain address"
            >
              <Input
                placeholder="..."
                style={{
                  background: COLORS.bgPrimary,
                  color: COLORS.textPrimary,
                  borderColor: COLORS.border,
                }}
              />
            </Form.Item>

            <Form.Item
              label="TRON Custodial Wallet Address"
              name="tronCustodialWallet"
              tooltip="TRON blockchain address"
            >
              <Input
                placeholder="T..."
                style={{
                  background: COLORS.bgPrimary,
                  color: COLORS.textPrimary,
                  borderColor: COLORS.border,
                }}
              />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                htmlType="submit"
                loading={defaultMerchantLoading}
                style={{
                  background: COLORS.accent,
                  borderColor: COLORS.accent,
                }}
              >
                Save Custodial Wallets
              </Button>
            </Form.Item>
          </Form>
        ) : (
          <div
            style={{
              textAlign: "center",
              padding: "40px 0",
              color: COLORS.textSecondary,
            }}
          >
            <Text>
              Default merchant not found. Please ensure the default merchant is
              configured.
            </Text>
          </div>
        )}
      </StyledCard>

      {/* App Settings */}
      <StyledCard title="Application Settings" loading={loading}>
        {settings.length === 0 && !loading ? (
          <div
            style={{
              textAlign: "center",
              padding: "40px 0",
              color: COLORS.textSecondary,
            }}
          >
            <Text>
              No settings found. Settings will be created automatically on
              server startup.
            </Text>
          </div>
        ) : (
          settings
            .filter(
              (setting) =>
                setting.key !== "rsaPublicKey" &&
                setting.key !== "rsaPrivateKey" &&
                setting.key !== "webHookRsaPublicKey" &&
                setting.key !== "webhookRsaSecretKey" &&
                setting.key !== "IS_CRON_ROBOT_ACTIVE",
            )
            .map((setting) => (
              <SettingItem key={setting.id}>
                <div className="setting-info">
                  <div className="setting-label">
                    {setting.key}
                    {setting.category && (
                      <Tag color="blue" style={{ marginLeft: 8 }}>
                        {setting.category}
                      </Tag>
                    )}
                  </div>
                  {setting.description && (
                    <div className="setting-description">
                      {setting.description}
                    </div>
                  )}
                  {!isBooleanSetting(setting.value) &&
                    renderSettingValue(setting)}
                </div>
                <div>
                  {isBooleanSetting(setting.value) ? (
                    renderSettingValue(setting)
                  ) : (
                    <Button
                      icon={<EditOutlined />}
                      onClick={() => handleEdit(setting)}
                      size="small"
                    >
                      Edit
                    </Button>
                  )}
                </div>
              </SettingItem>
            ))
        )}
      </StyledCard>

      {/* Edit Modal */}
      <Modal
        title={`Edit Setting: ${editingSetting?.key}`}
        open={editModalVisible}
        onOk={handleSave}
        onCancel={() => setEditModalVisible(false)}
        okText="Save"
        cancelText="Cancel"
        bodyStyle={{
          background: COLORS.bgSecondary,
          color: COLORS.textPrimary,
        }}
        style={{ top: 20 }}
      >
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary">{editingSetting?.description}</Text>
        </div>
        <Input
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          placeholder="Enter value"
          style={{
            background: COLORS.bgPrimary,
            color: COLORS.textPrimary,
            borderColor: COLORS.border,
          }}
        />
      </Modal>

      {/* RSA Key Pair Generation Confirmation Modal */}
      <Modal
        title="Generate New RSA Key Pair"
        open={rsaConfirmModalVisible}
        onOk={confirmGenerateRSAKeyPair}
        onCancel={() => setRsaConfirmModalVisible(false)}
        okText="Generate"
        cancelText="Cancel"
        okButtonProps={{ danger: true }}
        bodyStyle={{
          background: COLORS.bgSecondary,
          color: COLORS.textPrimary,
        }}
        style={{ top: 20 }}
      >
        <div style={{ marginBottom: 16 }}>
          <Text
            strong
            style={{
              color: COLORS.textPrimary,
              fontSize: 16,
              display: "block",
              marginBottom: 12,
            }}
          >
            ⚠️ Warning
          </Text>
          <Text
            style={{
              color: COLORS.textPrimary,
              display: "block",
              marginBottom: 8,
            }}
          >
            Are you sure you want to generate a new RSA key pair?
          </Text>
          <Text style={{ color: COLORS.textSecondary, display: "block" }}>
            This action will replace the existing RSA key pair. All merchants
            will need to update their public keys to continue verifying webhook
            signatures.
          </Text>
        </div>
      </Modal>

      {/* Logout All Admins Confirmation Modal */}
      <Modal
        title="Logout All Administrators"
        open={logoutAllAdminsModalVisible}
        onOk={handleLogoutAllAdmins}
        onCancel={() => setLogoutAllAdminsModalVisible(false)}
        okText="Logout All"
        cancelText="Cancel"
        okButtonProps={{ danger: true }}
        bodyStyle={{
          background: COLORS.bgSecondary,
          color: COLORS.textPrimary,
        }}
        style={{ top: 20 }}
      >
        <div style={{ marginBottom: 16 }}>
          <Text
            strong
            style={{
              color: COLORS.textPrimary,
              fontSize: 16,
              display: "block",
              marginBottom: 12,
            }}
          >
            ⚠️ Warning
          </Text>
          <Text
            style={{
              color: COLORS.textPrimary,
              display: "block",
              marginBottom: 8,
            }}
          >
            Are you sure you want to logout all administrators?
          </Text>
          <Text style={{ color: COLORS.textSecondary, display: "block" }}>
            This action will invalidate all active administrator sessions except
            your own. All administrators will need to log in again.
          </Text>
        </div>
      </Modal>

      {/* Logout All Merchants Confirmation Modal */}
      <Modal
        title="Logout All Merchants"
        open={logoutAllMerchantsModalVisible}
        onOk={handleLogoutAllMerchants}
        onCancel={() => setLogoutAllMerchantsModalVisible(false)}
        okText="Logout All"
        cancelText="Cancel"
        okButtonProps={{ danger: true }}
        bodyStyle={{
          background: COLORS.bgSecondary,
          color: COLORS.textPrimary,
        }}
        style={{ top: 20 }}
      >
        <div style={{ marginBottom: 16 }}>
          <Text
            strong
            style={{
              color: COLORS.textPrimary,
              fontSize: 16,
              display: "block",
              marginBottom: 12,
            }}
          >
            ⚠️ Warning
          </Text>
          <Text
            style={{
              color: COLORS.textPrimary,
              display: "block",
              marginBottom: 8,
            }}
          >
            Are you sure you want to logout all merchants?
          </Text>
          <Text style={{ color: COLORS.textSecondary, display: "block" }}>
            This action will invalidate all active merchant sessions. All
            merchants will need to log in again.
          </Text>
        </div>
      </Modal>

      {/* Webhook IPs Configuration Modal */}
      <Modal
        title="Configure Webhook IPs"
        open={webhookIPsModalVisible}
        onOk={handleUpdateWebhookIPs}
        onCancel={() => setWebhookIPsModalVisible(false)}
        okText="Save IPs"
        cancelText="Cancel"
        bodyStyle={{
          background: COLORS.bgSecondary,
          color: COLORS.textPrimary,
        }}
        style={{ top: 20 }}
      >
        <div style={{ marginBottom: 16 }}>
          <Text
            style={{
              color: COLORS.textSecondary,
              display: "block",
              marginBottom: 12,
            }}
          >
            Enter IP addresses (IPv4 or IPv6) from which webhooks will be sent.
            Separate multiple IPs with commas, spaces, or new lines.
          </Text>
          <TextArea
            value={webhookIPsInput}
            onChange={(e) => setWebhookIPsInput(e.target.value)}
            placeholder="192.168.1.1&#10;10.0.0.1&#10;2001:db8::1"
            rows={6}
            style={{
              background: COLORS.bgPrimary,
              color: COLORS.textPrimary,
              borderColor: COLORS.border,
              fontFamily: "monospace",
            }}
          />
          <Text
            style={{
              color: COLORS.textSecondary,
              fontSize: 12,
              display: "block",
              marginTop: 8,
            }}
          >
            Example: 192.168.1.1, 10.0.0.1, 2001:db8::1
          </Text>
        </div>
      </Modal>

      {/* Birthday Email Configuration Modal */}
      <BirthdayEmailConfigModal
        visible={birthdayEmailConfigVisible}
        onClose={() => setBirthdayEmailConfigVisible(false)}
      />
    </PageContainer>
  );
};

export default Settings;
